import { useState, useEffect, useRef } from 'react';
import { Mic, Square, Loader2, Sparkles, Radio, BookOpen, X, LayoutTemplate, ChevronRight, AlertTriangle, Keyboard, Pause, Play } from 'lucide-react';
import { useAudioRecorder } from '../../hooks/useAudioRecorder';
import { useSpeechRecognition } from '../../hooks/useSpeechRecognition';
import { useReportStore } from '../../stores/reportStore';
import { usePreferencesStore } from '../../stores/preferencesStore';
import { transcriptionApi, reportsApi } from '../../services/api';
import { detectDictationGaps } from '../../lib/dictationGaps';
import { EXAM_NOTE_OPTIONS, modelLabel } from '../../types';
import type { ClassifyResponse } from '../../types';
import ModeSelector from './ModeSelector';
import TranscriptionDisplay from './TranscriptionDisplay';
import ConfirmTemplateModal from './ConfirmTemplateModal';

// Optional keyboard hotkey to start/stop recording. Per-device setting stored
// in localStorage (no backend round-trip). Works while this tab is focused —
// a web page can't capture OS-global keys without a native helper.
const HOTKEY_LS = 'rd_dictation_hotkey_v4';
// Fixed shortcut: Ctrl+1 = Generate/Finish (Ctrl combos fire everywhere, incl.
// the editor). The mic key (Ctrl+`) is the configurable one below.
const GENERATE_KEY = 'Ctrl+1';

// Silent template prediction: at/above this confidence the predicted template
// is applied without the confirm modal. Below it (or an "unknown" exam), the
// radiologist still gets the confirm gate so an ambiguous study can't silently
// land on the wrong template. Deterministic (>=0.85) and LLM (>=0.9) matches
// clear this; low-confidence sklearn/fallbacks do not.
const SILENT_CONFIDENCE_THRESHOLD = 0.7;

function Kbd({ children }: { children: string }) {
  return (
    <kbd className="px-1.5 py-0.5 rounded bg-gray-100 dark:bg-surface-800 border border-gray-300 dark:border-surface-700 font-mono text-[10px] text-gray-700 dark:text-gray-200">
      {children}
    </kbd>
  );
}

interface HotkeyConfig {
  enabled: boolean;
  key: string;
}

function loadHotkey(): HotkeyConfig {
  try {
    const raw = localStorage.getItem(HOTKEY_LS);
    if (raw) {
      const v = JSON.parse(raw);
      if (typeof v?.key === 'string') return { enabled: !!v.enabled, key: v.key };
    }
  } catch {
    /* ignore corrupt/unavailable storage */
  }
  return { enabled: true, key: 'Ctrl+`' };
}

/** Normalise a keydown into a stable hotkey string, e.g. "F4", "Ctrl+Space". */
function formatHotkey(e: KeyboardEvent): string {
  const parts: string[] = [];
  if (e.ctrlKey) parts.push('Ctrl');
  if (e.altKey) parts.push('Alt');
  if (e.shiftKey) parts.push('Shift');
  if (e.metaKey) parts.push('Meta');
  let key = e.key;
  if (key === ' ') key = 'Space';
  else if (key.length === 1) key = key.toUpperCase();
  parts.push(key);
  return parts.join('+');
}

export default function DictationPanel() {
  const {
    isRecording,
    isPaused,
    duration,
    startRecording,
    stopRecording,
    pauseRecording,
    resumeRecording,
    resetRecording,
    error: recordingError,
  } = useAudioRecorder();

  const {
    isListening,
    transcript: liveTranscript,
    interimTranscript,
    isSupported: speechSupported,
    startListening,
    stopListening,
    resetTranscript,
    error: speechError,
  } = useSpeechRecognition();

  const {
    transcription,
    resetNonce,
    selectedTemplateId,
    selectedTemplateName,
    setSelectedTemplate,
    setTemplatesPanelOpen,
    isTranscribing,
    isGenerating,
    modelUsed,
    primaryModel,
    templateId: lastUsedTemplateId,
    setTranscription,
    setModality,
    setBodyPart,
    setIsTranscribing,
    setIsGenerating,
    setRawReport,
    setGeneratedReport,
    setIssues,
    setDebugPrompt,
    setDebugUserMessage,
    setExamCategory,
    setTemplateId,
    setTemplateName,
    setDecisionId,
    setClassificationMethod,
    setClassificationConfidence,
    setModelUsed,
    setPrimaryModel,
  } = useReportStore();

  const { preferences } = usePreferencesStore();
  const [error, setError] = useState<string | null>(null);
  const [useLivePreview, setUseLivePreview] = useState(true);
  const [showStyleGuide, setShowStyleGuide] = useState(false);
  const [hotkey, setHotkey] = useState<HotkeyConfig>(loadHotkey);
  const [capturingHotkey, setCapturingHotkey] = useState(false);

  // Mic mode comes from the user's saved preference (the "Mode" selector in
  // the panel header):
  //  'open'  = click Record, mic stays on until Stop (long dictations);
  //  'push'  = hold the button while speaking, release to transcribe;
  //  'pause' = click Record, then Pause/Resume as many times as needed within
  //            ONE continuous recording, and Finish to transcribe + submit.
  const micMode: 'open' | 'push' | 'pause' =
    preferences?.voice_input_mode === 'push_to_talk' ? 'push'
    : preferences?.voice_input_mode === 'pause_resume' ? 'pause'
    : 'open';
  const recordStartedAtRef = useRef<number>(0);
  // Live-preview text committed across pauses, so the preview doesn't reset
  // each time the radiologist resumes (the final Whisper pass on the full
  // blob is authoritative either way).
  const liveAccumRef = useRef('');

  // 2-pass confirmation gate state
  const [isClassifying, setIsClassifying] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [classifyResult, setClassifyResult] = useState<ClassifyResponse | null>(null);
  const [pendingTranscription, setPendingTranscription] = useState('');

  // Template selection lives in the store, shared with the docked template
  // rail (the single templates surface — no modal).

  // Update transcription with live preview while recording (not while paused).
  // Prepend any text accumulated from earlier segments so pausing/resuming
  // keeps the running preview instead of restarting it.
  useEffect(() => {
    if (isRecording && !isPaused && useLivePreview && speechSupported) {
      const segment = liveTranscript + (interimTranscript ? ' ' + interimTranscript : '');
      const fullLive = `${liveAccumRef.current} ${segment}`.trim();
      if (fullLive) {
        setTranscription(fullLive);
      }
    }
  }, [liveTranscript, interimTranscript, isRecording, isPaused, useLivePreview, speechSupported]);

  // "New Report" (resetSession) bumps resetNonce — tear down any in-progress
  // capture so a half-finished dictation isn't carried into the next report.
  const didMountRef = useRef(false);
  useEffect(() => {
    if (!didMountRef.current) { didMountRef.current = true; return; }
    if (speechSupported) stopListening();
    if (isRecording) stopRecording().catch(() => {});
    resetRecording();
    resetTranscript();
    liveAccumRef.current = '';
    setError(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resetNonce]);

  // Pass 2: the actual authoring call, against a confirmed template.
  const doGenerate = async (text: string, forcePrimary: boolean, templateId: string) => {
    if (!text.trim()) {
      setError('Please dictate some findings first');
      return;
    }
    setIsGenerating(true);
    setError(null);

    try {
      // No model in the request: the backend resolves it per-account
      // (users.llm_model, admin-set) falling back to the system default.
      const response = await reportsApi.generate({
        transcription: text,
        template_id: templateId || undefined,
        force_primary: forcePrimary || undefined,
      });

      setRawReport(response.raw_report);
      setGeneratedReport(response.report);
      // Merge client-detected dictation-gap markers ([unclear: "..."]) with
      // backend validation issues; gaps first. The issues pane itself is
      // feature-flagged off (too obstructive per radiologist, 2026-07-21) —
      // issues are still collected so the pane redesign can pick them up.
      const gaps = detectDictationGaps(response.report);
      setIssues([...gaps, ...response.issues]);
      if (response.debug_prompt) setDebugPrompt(response.debug_prompt);
      if (response.debug_user_message) setDebugUserMessage(response.debug_user_message);
      if (response.modality) setModality(response.modality);
      if (response.body_part) setBodyPart(response.body_part);

      // Store template classification info
      if (response.exam_category) setExamCategory(response.exam_category);
      if (response.template_id) setTemplateId(response.template_id);
      if (response.template_name) setTemplateName(response.template_name);
      if (response.decision_id) setDecisionId(response.decision_id);
      if (response.classification_method) setClassificationMethod(response.classification_method);
      if (response.classification_confidence !== undefined) setClassificationConfidence(response.classification_confidence);

      // Fallback chain transparency
      setModelUsed(response.model_used || '');
      setPrimaryModel(response.primary_model || '');

      // Clear the template selection so the NEXT dictation is classified
      // fresh. Otherwise this report's template stays "stuck" as the default
      // in the confirm gate for every subsequent report (radiologist
      // feedback 2026-06-16). A manual pick still applies to the report it
      // was made for — it's only cleared once that report is generated.
      setSelectedTemplate('', '');
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Report generation failed';
      // If the user explicitly asked for the primary and it failed, give them a clearer hint.
      setError(forcePrimary
        ? `${primaryModel ? modelLabel(primaryModel) : 'Primary model'} is still overloaded — try again in a moment, or keep the existing report.`
        : msg);
    } finally {
      setIsGenerating(false);
    }
  };

  // Pass 1 + confirmation gate. BOTH generation entry points (the Generate
  // button and auto-generate after recording) route through here, so the
  // radiologist always confirms the template before authoring.
  const startGeneration = async (overrideTranscription?: string, forcePrimary = false) => {
    const text = overrideTranscription ?? transcription;
    if (!text.trim()) {
      setError('Please dictate some findings first');
      return;
    }
    // Retry-with-primary reuses the already-confirmed template — no re-gate.
    // Prefer the template the last generation actually used (from the
    // response) over the rail selection, which can be empty and would send
    // an ungated, template-less request.
    if (forcePrimary) {
      doGenerate(text, true, lastUsedTemplateId || selectedTemplateId);
      return;
    }

    setError(null);
    setPendingTranscription(text);
    setClassifyResult(null);

    // If the radiologist has explicitly picked a template in the rail, that's a
    // manual override — honour it and generate directly, no confirm needed.
    if (selectedTemplateId) {
      doGenerate(text, false, selectedTemplateId);
      return;
    }

    setIsClassifying(true);
    try {
      const result = await reportsApi.classify(text);
      if (result.classification_confidence !== undefined) {
        setClassificationConfidence(result.classification_confidence);
      }

      // Silent template prediction: when the classifier is confident and
      // returns a concrete, known template, skip the confirm modal and go
      // straight to generation. Reflect the choice in the rail so it stays
      // visible and overridable. Only fall back to the confirm gate when the
      // classifier is unsure (low confidence or "unknown").
      const confident =
        (result.classification_confidence ?? 0) >= SILENT_CONFIDENCE_THRESHOLD &&
        !!result.template_id &&
        !!result.exam_category &&
        result.exam_category !== 'unknown';

      if (confident && result.template_id) {
        setSelectedTemplate(result.template_id, result.template_name || '');
        doGenerate(text, false, result.template_id);
        return;
      }

      // Uncertain — let the radiologist confirm or override.
      setClassifyResult(result);
      setShowConfirm(true);
    } catch {
      // Classification failed — still let the radiologist pick a template.
      setClassifyResult({ recommended_source: 'classifier', has_profile: false });
      setShowConfirm(true);
    } finally {
      setIsClassifying(false);
    }
  };

  const handleConfirmTemplate = (templateId: string, templateName: string) => {
    setShowConfirm(false);
    setSelectedTemplate(templateId, templateName); // reflect in rail + dictation field
    doGenerate(pendingTranscription, false, templateId);
  };

  const handleCancelConfirm = () => {
    setShowConfirm(false);
    setClassifyResult(null);
  };

  const handleRegenerateWithPrimary = () => startGeneration(undefined, true);

  const beginRecording = async () => {
    try {
      // Clear previous transcription for new recording
      setTranscription('');
      resetTranscript();
      liveAccumRef.current = '';

      await startRecording();
      recordStartedAtRef.current = Date.now();

      // Start live speech recognition for preview
      if (speechSupported && useLivePreview) {
        startListening();
      }

      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to start recording');
    }
  };

  const endRecordingAndTranscribe = async () => {
    try {
      // Stop live speech recognition
      if (speechSupported && useLivePreview) {
        stopListening();
      }

      const audioBlob = await stopRecording();

      // Push-to-talk: a sub-300ms press is an accidental tap, not a
      // dictation — discard rather than sending useless audio to Whisper.
      if (micMode === 'push' && Date.now() - recordStartedAtRef.current < 300) {
        resetRecording();
        resetTranscript();
        setError('Hold the button down while dictating, release to finish.');
        return;
      }

      setIsTranscribing(true);
      setError(null);

      // Use Whisper for accurate final transcription
      const result = await transcriptionApi.upload(audioBlob);

      // A 200 with empty text (silence / VAD trim / hiccup) must NOT overwrite
      // the good live transcript and silently abort generation. Preserve what
      // we have and tell the radiologist to retry.
      if (!(result.transcription || '').trim()) {
        resetRecording();
        setIsTranscribing(false);
        setError('No speech detected in the recording — please dictate your findings and try again.');
        return;
      }

      setTranscription(result.transcription);
      resetRecording();
      resetTranscript();
      setIsTranscribing(false);

      // Auto-generate report from Whisper result (via the confirmation gate)
      startGeneration(result.transcription);
    } catch (err) {
      const detail = (err as { response?: { data?: { detail?: unknown } } })?.response?.data?.detail;
      setError(
        typeof detail === 'string' ? detail : err instanceof Error ? err.message : 'Transcription failed'
      );
      // Keep live transcript if Whisper fails
      setIsTranscribing(false);
    }
  };

  // Open-mic mode: click toggles. Recording runs until the radiologist
  // explicitly stops — no duration cap (long dictations are the point).
  const handleToggleRecording = () => {
    if (isRecording) {
      endRecordingAndTranscribe();
    } else {
      beginRecording();
    }
  };

  // Pause/resume mode: one continuous recording the radiologist can pause and
  // resume any number of times. We also pause/resume the live-preview speech
  // recognition (it taps the system mic independently of the MediaRecorder,
  // so it must be stopped during a pause or it would hear speech the
  // recording doesn't capture). The committed preview text is preserved.
  const handlePause = () => {
    if (!isRecording || isPaused) return;
    if (speechSupported && useLivePreview) {
      const segment = liveTranscript + (interimTranscript ? ' ' + interimTranscript : '');
      liveAccumRef.current = `${liveAccumRef.current} ${segment}`.trim();
      stopListening();
    }
    pauseRecording();
  };

  const handleResume = () => {
    if (!isRecording || !isPaused) return;
    resumeRecording();
    if (speechSupported && useLivePreview) {
      resetTranscript();
      startListening();
    }
  };

  // Persist the hotkey setting per-device.
  useEffect(() => {
    try {
      localStorage.setItem(HOTKEY_LS, JSON.stringify(hotkey));
    } catch {
      /* ignore */
    }
  }, [hotkey]);

  // Global hotkey. In Pause/Resume mode it walks Record → Pause → Resume →
  // Pause … (Finish stays a deliberate click); in the other modes it toggles
  // record/stop. Also a capture mode to rebind it. Re-subscribes when the
  // relevant state changes so the handler always sees the latest state.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      // Ignore lone modifier presses.
      if (e.key === 'Control' || e.key === 'Alt' || e.key === 'Shift' || e.key === 'Meta') return;

      // Rebind mode: the next key becomes the hotkey (Esc cancels).
      if (capturingHotkey) {
        e.preventDefault();
        if (e.key === 'Escape') {
          setCapturingHotkey(false);
          return;
        }
        setHotkey({ enabled: true, key: formatHotkey(e) });
        setCapturingHotkey(false);
        return;
      }

      // Generate / Finish shortcut (Ctrl+2). While recording it finishes the
      // dictation; otherwise it generates from the current transcription.
      if (formatHotkey(e) === GENERATE_KEY) {
        e.preventDefault();
        if (isTranscribing || isGenerating) return;
        if (isRecording) {
          endRecordingAndTranscribe();
        } else {
          const t = useReportStore.getState().transcription;
          if (t.trim()) startGeneration(t);
        }
        return;
      }

      if (!hotkey.enabled || formatHotkey(e) !== hotkey.key) return;

      // Don't hijack normal typing: a bare ALPHANUMERIC key inside an editable
      // field should type, not toggle. Punctuation like the default ` / ~,
      // function keys, and modifier combos still fire everywhere (including the
      // report editor) — which is what you want for hands-free dictation.
      const bare = !e.ctrlKey && !e.altKey && !e.metaKey;
      const el = document.activeElement as HTMLElement | null;
      const editable =
        !!el && (el.tagName === 'INPUT' || el.tagName === 'TEXTAREA' || el.isContentEditable);
      if (bare && /^[a-zA-Z0-9]$/.test(e.key) && editable) return;

      e.preventDefault();
      if (isTranscribing || isGenerating) return;
      if (micMode === 'pause') {
        if (!isRecording) beginRecording();
        else if (isPaused) handleResume();
        else handlePause();
      } else {
        handleToggleRecording();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [hotkey, capturingHotkey, micMode, isRecording, isPaused, isTranscribing, isGenerating]);

  // Push-to-talk mode: hold to record, release to transcribe. Leaving the
  // button while held also stops — otherwise the mic would be stuck open.
  const handlePushStart = () => {
    if (!isRecording && !isTranscribing && !isGenerating) {
      beginRecording();
    }
  };
  const handlePushEnd = () => {
    if (isRecording) {
      endRecordingAndTranscribe();
    }
  };

  const formatDuration = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  return (
    <div className="space-y-4">
      {/* Pass-1 confirmation gate */}
      <ConfirmTemplateModal
        isOpen={showConfirm}
        classifyResult={classifyResult}
        isClassifying={isClassifying}
        initialTemplateId={selectedTemplateId}
        onConfirm={handleConfirmTemplate}
        onCancel={handleCancelConfirm}
      />

      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-1">
        <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
          Dictation
        </h2>
        <ModeSelector />
      </div>

      {/* Template Selector */}
      <div>
        <label className="block text-sm text-gray-600 dark:text-gray-400 mb-1 flex items-center gap-1">
          <LayoutTemplate size={14} />
          Report Template
        </label>
        <button
          onClick={() => setTemplatesPanelOpen(true)}
          className="w-full flex items-center justify-between px-3 py-2 bg-white dark:bg-surface-850 border border-gray-300 dark:border-surface-800 rounded-lg text-sm text-gray-900 dark:text-white hover:border-gray-400 dark:hover:border-surface-700 focus:outline-none focus:ring-2 focus:ring-primary-500 transition-colors"
        >
          <span className={selectedTemplateName ? '' : 'text-gray-400 dark:text-gray-500'}>
            {selectedTemplateName || 'Auto-detect'}
          </span>
          <ChevronRight size={14} className="text-gray-400" />
        </button>
      </div>

      {/* Live Preview Toggle */}
      {speechSupported && (
        <div className="flex items-center gap-2">
          <input
            type="checkbox"
            id="livePreview"
            checked={useLivePreview}
            onChange={(e) => setUseLivePreview(e.target.checked)}
            className="w-4 h-4 text-primary-600 rounded focus:ring-primary-500"
          />
          <label htmlFor="livePreview" className="text-sm text-gray-600 dark:text-gray-400 flex items-center gap-1">
            <Radio size={14} />
            Live transcription preview
          </label>
        </div>
      )}

      {/* Recording hotkey (optional): one key toggles start/stop while this
          tab is focused. Click the key chip, then press a key to rebind. */}
      <div className="flex items-center gap-2 flex-wrap">
        <input
          type="checkbox"
          id="dictationHotkey"
          checked={hotkey.enabled}
          onChange={(e) => setHotkey((h) => ({ ...h, enabled: e.target.checked }))}
          className="w-4 h-4 text-primary-600 rounded focus:ring-primary-500"
        />
        <label htmlFor="dictationHotkey" className="text-sm text-gray-600 dark:text-gray-400 flex items-center gap-1">
          <Keyboard size={14} />
          Record hotkey
        </label>
        <button
          type="button"
          onClick={() => setCapturingHotkey((c) => !c)}
          title="Click, then press the key you want to start/stop recording (Esc to cancel)"
          className={`px-2 py-0.5 rounded border text-xs font-mono font-medium transition-colors ${
            capturingHotkey
              ? 'border-primary-500 text-primary-600 dark:text-primary-300 animate-pulse'
              : 'border-gray-300 dark:border-surface-700 text-gray-700 dark:text-gray-300 hover:border-gray-400 dark:hover:border-surface-600'
          }`}
        >
          {capturingHotkey ? 'Press a key…' : hotkey.key}
        </button>
        {hotkey.enabled && !capturingHotkey && (
          <span className="text-xs text-gray-400 dark:text-gray-500">
            {micMode === 'pause' ? 'press to record, then pause/resume' : 'press to start, again to stop'}
          </span>
        )}
      </div>

      {/* Keyboard shortcuts legend */}
      <div className="flex items-center gap-x-3 gap-y-1 flex-wrap text-xs text-gray-500 dark:text-gray-400">
        <span className="font-medium text-gray-600 dark:text-gray-300">Shortcuts</span>
        <span className="inline-flex items-center gap-1"><Kbd>{hotkey.key}</Kbd> Record / Pause / Resume</span>
        <span className="inline-flex items-center gap-1"><Kbd>Ctrl+1</Kbd> Generate</span>
        <span className="inline-flex items-center gap-1"><Kbd>Ctrl+2</Kbd> Send to Karisma</span>
        <span className="inline-flex items-center gap-1"><Kbd>Ctrl+3</Kbd> New report</span>
        <span className="text-gray-400 dark:text-gray-500">(while this tab is focused)</span>
      </div>

      {/* Recording Controls */}
      <div className="flex items-center gap-3 min-w-0">
        {micMode === 'pause' ? (
          isTranscribing || isGenerating ? (
            <button
              disabled
              className="flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium whitespace-nowrap select-none bg-primary-600 text-white opacity-50"
            >
              <Loader2 size={18} className="animate-spin" />
              {isTranscribing ? 'Transcribing...' : 'Generating...'}
            </button>
          ) : !isRecording ? (
            <button
              onClick={beginRecording}
              className="flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium whitespace-nowrap transition-colors select-none bg-primary-600 hover:bg-primary-700 text-white"
            >
              <Mic size={18} />
              Record
            </button>
          ) : (
            <>
              <button
                onClick={isPaused ? handleResume : handlePause}
                className={`flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium whitespace-nowrap transition-colors select-none text-white ${
                  isPaused ? 'bg-primary-600 hover:bg-primary-700' : 'bg-amber-500 hover:bg-amber-600'
                }`}
              >
                {isPaused ? (
                  <>
                    <Play size={18} className="fill-current" />
                    Resume
                  </>
                ) : (
                  <>
                    <Pause size={18} className="fill-current" />
                    Pause
                  </>
                )}
              </button>
              <button
                onClick={endRecordingAndTranscribe}
                className="flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium whitespace-nowrap transition-colors select-none bg-red-600 hover:bg-red-700 text-white"
              >
                <Square size={18} className="fill-current" />
                Finish
              </button>
            </>
          )
        ) : (
          <button
            {...(micMode === 'open'
              ? { onClick: handleToggleRecording }
              : {
                  onMouseDown: handlePushStart,
                  onMouseUp: handlePushEnd,
                  onMouseLeave: handlePushEnd,
                  onTouchStart: (e: { preventDefault(): void }) => { e.preventDefault(); handlePushStart(); },
                  onTouchEnd: (e: { preventDefault(): void }) => { e.preventDefault(); handlePushEnd(); },
                })}
            disabled={isTranscribing || isGenerating}
            className={`flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium whitespace-nowrap transition-colors select-none ${
              isRecording
                ? 'bg-red-600 hover:bg-red-700 text-white'
                : 'bg-primary-600 hover:bg-primary-700 text-white'
            } disabled:opacity-50`}
          >
            {isRecording ? (
              micMode === 'push' ? (
                <>
                  <Mic size={18} className="animate-pulse" />
                  Release to finish
                </>
              ) : (
                <>
                  <Square size={18} className="fill-current" />
                  Stop
                </>
              )
            ) : isTranscribing ? (
              <>
                <Loader2 size={18} className="animate-spin" />
                Transcribing...
              </>
            ) : isGenerating ? (
              <>
                <Loader2 size={18} className="animate-spin" />
                Generating...
              </>
            ) : micMode === 'push' ? (
              <>
                <Mic size={18} />
                Hold to talk
              </>
            ) : (
              <>
                <Mic size={18} />
                Record
              </>
            )}
          </button>
        )}

        {isRecording && (
          <div className="flex items-center gap-2 min-w-0">
            <div
              className={`w-3 h-3 rounded-full flex-shrink-0 ${
                isPaused ? 'bg-amber-500' : 'bg-red-500 animate-pulse-recording'
              }`}
            />
            <span className="text-sm font-medium text-gray-600 dark:text-gray-300">
              {formatDuration(duration)}
            </span>
            {isPaused ? (
              <span className="text-xs font-medium text-amber-600 dark:text-amber-400">
                Paused
              </span>
            ) : (
              isListening && useLivePreview && (
                <span className="text-xs text-green-600 dark:text-green-400 flex items-center gap-1">
                  <Radio size={12} className="animate-pulse" />
                  Live
                </span>
              )
            )}
            {micMode === 'open' && (
              <span className="text-xs text-gray-400 dark:text-gray-500 truncate">
                Mic stays open — click Stop when finished
              </span>
            )}
            {micMode === 'pause' && !isPaused && (
              <span className="text-xs text-gray-400 dark:text-gray-500 truncate">
                Pause/Resume as needed — Finish to submit
              </span>
            )}
          </div>
        )}

        {/* Style Guide toggle */}
        {!isRecording && (
          <button
            onClick={() => setShowStyleGuide(!showStyleGuide)}
            className={`flex items-center gap-1.5 px-3 py-2.5 rounded-lg text-sm font-medium whitespace-nowrap transition-colors ml-auto ${
              showStyleGuide
                ? 'bg-amber-100 dark:bg-amber-900/20 text-amber-700 dark:text-amber-300 ring-1 ring-amber-300 dark:ring-amber-800/40'
                : 'bg-gray-100 dark:bg-surface-850 hover:bg-gray-200 dark:hover:bg-surface-800 text-gray-600 dark:text-gray-300'
            }`}
          >
            <BookOpen size={16} />
            Style
          </button>
        )}
      </div>

      {/* Regenerate button — below recording controls */}
      {!isRecording && transcription.trim() && !isGenerating && (
        <button
          onClick={() => startGeneration()}
          disabled={isClassifying}
          className="flex items-center justify-center gap-2 w-full px-4 py-2.5 bg-primary-600 hover:bg-primary-700 disabled:opacity-60 text-white rounded-lg text-sm font-medium transition-colors"
        >
          {isClassifying ? (
            <>
              <Loader2 size={16} className="animate-spin" />
              Selecting template…
            </>
          ) : (
            <>
              <Sparkles size={16} />
              Generate Report
            </>
          )}
        </button>
      )}

      {/* Degraded-capacity notice — only when the fallback chain produced a non-primary report */}
      {!isRecording && modelUsed && primaryModel && modelUsed !== primaryModel && (
        <div className="flex items-start gap-2 p-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800/40 rounded-lg text-xs">
          <AlertTriangle size={16} className="text-amber-600 dark:text-amber-400 flex-shrink-0 mt-0.5" />
          <div className="flex-1 min-w-0">
            <p className="text-amber-800 dark:text-amber-200 font-medium mb-0.5">
              Generated by {modelLabel(modelUsed)}
            </p>
            <p className="text-amber-700 dark:text-amber-300/80 mb-2">
              {modelLabel(primaryModel)} was overloaded. Quality may be lower — review carefully or retry below.
            </p>
            <button
              onClick={handleRegenerateWithPrimary}
              disabled={isGenerating}
              className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-amber-600 hover:bg-amber-700 disabled:opacity-50 disabled:cursor-not-allowed text-white rounded text-xs font-medium transition-colors"
            >
              <Sparkles size={12} />
              Retry with {modelLabel(primaryModel)}
            </button>
          </div>
        </div>
      )}

      {/* Style Guide Panel */}
      {showStyleGuide && (
        <div className="p-4 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800/40 rounded-lg text-sm">
          <div className="flex items-center justify-between mb-3">
            <h4 className="font-medium text-amber-800 dark:text-amber-200 flex items-center gap-2">
              <BookOpen size={16} />
              Active Style Guide
            </h4>
            <button
              onClick={() => setShowStyleGuide(false)}
              className="p-1 text-amber-600 dark:text-amber-400 hover:text-amber-800 dark:hover:text-amber-200"
            >
              <X size={16} />
            </button>
          </div>

          <div className="space-y-2 text-amber-700 dark:text-amber-300">
            <div className="flex items-center gap-2">
              <span className="font-medium">Reporting Style:</span>
              <span className="px-2 py-0.5 rounded bg-amber-200 dark:bg-amber-800 text-xs font-medium capitalize">
                {preferences?.reporting_style || 'narrative'}
              </span>
            </div>

            <div>
              <span className="font-medium">Report Sections:</span>
              <div className="mt-1 font-mono text-xs bg-amber-100 dark:bg-amber-900/40 rounded p-2 space-y-0.5">
                <div className="font-bold">CT ABDOMEN AND PELVIS (WITH CONTRAST)</div>
                <div>Clinical History</div>
                <div>Comparison</div>
                <div>Report</div>
                <div>Conclusion</div>
              </div>
            </div>

            {(() => {
              const activeNotes = (preferences?.exam_notes || '')
                .split(',')
                .map((k) => k.trim())
                .filter(Boolean);
              if (activeNotes.length === 0) return null;
              return (
                <div>
                  <span className="font-medium">Exam-Specific Notes:</span>
                  <ul className="mt-1 list-disc list-inside text-xs">
                    {activeNotes.map((key) => {
                      const opt = EXAM_NOTE_OPTIONS.find((o) => o.key === key);
                      return <li key={key}>{opt?.label || key}</li>;
                    })}
                  </ul>
                </div>
              );
            })()}
          </div>
        </div>
      )}

      {/* Error Display */}
      {(error || recordingError || speechError) && (
        <div className="p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800/40 rounded-lg text-red-700 dark:text-red-400 text-sm">
          {error || recordingError || speechError}
        </div>
      )}

      {/* Transcription Display */}
      <TranscriptionDisplay />

      {/* Live preview indicator */}
      {isRecording && interimTranscript && useLivePreview && (
        <div className="text-sm text-gray-500 italic">
          <span className="text-green-500">...</span> {interimTranscript}
        </div>
      )}
    </div>
  );
}
